import { VariableOption, VariableValueType } from '@/hooks';
import {
  Badge,
  Group,
  Select,
  Text,
  type SelectItem,
  useMantineTheme,
} from '@mantine/core';
import { forwardRef } from 'react';
import { PiBracketsCurlyBold } from 'react-icons/pi';
import { t } from 'i18next';
import {
  DropdownContainer,
  DropdownFooter,
  DropdownList,
  ItemLabel,
  SelectItemStyles,
} from './styles';

type ReferenceItem = SelectItem & {
  type: VariableValueType;
};

interface ReferenceSelectProps {
  data: VariableOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

const ReferenceSelectItem = forwardRef<HTMLDivElement, ReferenceItem>(
  ({ label, type, selected, ...others }, ref) => {
    return (
      <div ref={ref} {...others}>
        <Group position="apart" spacing="xs" noWrap>
          <ItemLabel size="xs">{label}</ItemLabel>
          <Badge size="xs" variant="light" radius="xs">
            <Text fw={400}>{type}</Text>
          </Badge>
        </Group>
      </div>
    );
  }
);

ReferenceSelectItem.displayName = 'ReferenceSelectItem';

export const ReferenceSelect = ({
  data,
  value,
  onChange,
  placeholder = t('graphEditor.common.selectReference'),
}: ReferenceSelectProps) => {
  const theme = useMantineTheme();
  const items: ReferenceItem[] = data.map((item) => ({
    value: item.value,
    label: item.label,
    type: item.type,
  }));

  const Dropdown = forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
  >(({ children, ...props }, ref) => (
    <DropdownContainer ref={ref} {...props}>
      <DropdownList>{children}</DropdownList>
      <DropdownFooter>
        <Text size={10} c="dimmed">
          {t('graphEditor.common.variablesAvailable', { count: data.length })}
        </Text>
      </DropdownFooter>
    </DropdownContainer>
  ));

  Dropdown.displayName = 'ReferenceSelectDropdown';

  return (
    <Select
      icon={<PiBracketsCurlyBold />}
      searchable
      data={items}
      value={value}
      onChange={onChange}
      itemComponent={ReferenceSelectItem}
      dropdownComponent={Dropdown}
      placeholder={placeholder}
      nothingFound={t('graphEditor.common.noReferencesFound')}
      size="sm"
      maxDropdownHeight={240}
      styles={SelectItemStyles(theme)}
    />
  );
};
